import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Chart } from 'chart.js/auto';
import { LogIn, Timer , Hourglass, XCircle, Clock } from 'lucide-react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import './SalesDashboard.css';

const BreakDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  const options = ['Tea Break', 'Lunch Break', 'Other'];
  const filteredOptions = options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <div 
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%', padding: '10px 12px', fontSize: '14px', border: '1px solid #e2e8f0', borderRadius: '8px', 
          background: '#fff', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}
      >
        <span style={{ color: value ? '#0f172a' : '#94a3b8' }}>{value || 'Select Break Type'}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
      </div>
      {isOpen && (
        <div style={{
          marginTop: '8px',
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.08)', zIndex: 50, padding: '8px'
        }}>
          <input 
            type="text"
            placeholder="Search break types..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px', fontSize: '13px', background: '#f8fafc',
              border: '1px solid #f1f5f9', borderRadius: '6px', outline: 'none', marginBottom: '8px'
            }}
          />
          <div style={{ minHeight: '200px', overflowY: 'auto' }}>
            <div 
              onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}
              style={{
                padding: '8px 12px', fontSize: '14px', cursor: 'pointer', borderRadius: '6px',
                background: !value ? '#dcfce7' : 'transparent',
                color: !value ? '#166534' : '#334155',
                fontWeight: !value ? 600 : 400
              }}
              onMouseEnter={e => { if (value) e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={e => { if (value) e.currentTarget.style.background = 'transparent'; }}
            >
              Select Break Type
            </div>
            {filteredOptions.map(opt => (
              <div 
                key={opt}
                onClick={() => { onChange(opt); setIsOpen(false); setSearch(''); }}
                style={{
                  padding: '8px 12px', fontSize: '14px', cursor: 'pointer', borderRadius: '6px',
                  background: value === opt ? '#dcfce7' : 'transparent',
                  color: value === opt ? '#166534' : '#334155',
                  fontWeight: value === opt ? 600 : 400
                }}
                onMouseEnter={e => { if (value !== opt) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (value !== opt) e.currentTarget.style.background = 'transparent'; }}
              >
                {opt}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                No matches found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function SalesDashboard() {
  const { user } = useAuth();
  const [isNarrowScreen, setIsNarrowScreen] = useState(() => {
    return window.innerWidth <= 1280;
  });
  const [loading, setLoading] = useState(true);
  const [leadStats, setLeadStats] = useState({ total: 0, byStatus: {} });
  const [dealStats, setDealStats] = useState({});
  const [myLeads, setMyLeads] = useState([]);
  const [metaLeads, setMetaLeads] = useState([]);
  const [salesAnalytics, setSalesAnalytics] = useState({});
  const [recentActivities, setRecentActivities] = useState([]);

  // Attendance states
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [checkInTimer, setCheckInTimer] = useState(0);
  const [activeBreak, setActiveBreak] = useState(null);
  const [hideBreakScreen, setHideBreakScreen] = useState(false);
  const [breakTimer, setBreakTimer] = useState(0);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakForm, setBreakForm] = useState({ type: 'Tea Break', reason: '' });
  const [breakDetailsModal, setBreakDetailsModal] = useState({ visible: false, type: '', breaks: [] });
  const [breakLimits, setBreakLimits] = useState({ totalBreakLimit: 60 });

  useEffect(() => {
    api.get('/attendance/break-limits').then(res => {
      if (res.data) {
        setBreakLimits({
          totalBreakLimit: res.data.totalBreakLimit ?? 60
        });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let interval;
    if (activeBreak && activeBreak.startTime && !activeBreak.endTime) {
      const updateBreakTimer = () => {
        const elapsed = Math.floor((Date.now() - new Date(activeBreak.startTime).getTime()) / 1000);
        setBreakTimer(elapsed);
      };
      updateBreakTimer();
      interval = setInterval(updateBreakTimer, 1000);
    } else {
      setBreakTimer(0);
    }
    return () => clearInterval(interval);
  }, [activeBreak]);

  const checkInIntervalRef = useRef(null);
  const checkedInThisSession = useRef(false);
  const [personalAttendance, setPersonalAttendance] = useState({ presentDays: 0, absentDays: 0, halfDays: 0, lateMarks: 0 });
  const [generalStats, setGeneralStats] = useState({
    activeProjects: 0, upcomingDeadlines: 0, goals: 0, broadcasts: 0,
    pendingApprovals: 0, pendingLeaves: 0
  });
  const [weeklyAnalytics, setWeeklyAnalytics] = useState({ days: [], goalsCompleted: [], completedProjects: [], newJoining: [], upcomingDeadlines: [] });
  const [recentLeaves, setRecentLeaves] = useState([]);

  // Chart refs
  const pipelineChartRef = useRef(null);
  const pipelineChartInst = useRef(null);
  const leadStagesChartRef = useRef(null);
  const leadStagesChartInst = useRef(null);
  const personalChartRef = useRef(null);
  const personalChartInst = useRef(null);
  const weeklyChartRef = useRef(null);
  const weeklyChartInst = useRef(null);

  // Timer logic
  // Rules: only Lunch/Tea breaks pause this timer; Other breaks do not.
  useEffect(() => {
    if (todayAttendance?.loginTime) {
      const updateTimer = () => {
        const endTime = todayAttendance.logoutTime ? new Date(todayAttendance.logoutTime) : new Date();
        let elapsed = Math.floor((endTime - new Date(todayAttendance.loginTime)) / 1000);
        let deductSecs = 0;

        if (todayAttendance.breaks && todayAttendance.breaks.length > 0) {
          todayAttendance.breaks.forEach(b => {
            if (b.breakType === 'Other') return; // ignore Other breaks

            let breakDur = 0;
            if (b.endTime) {
              breakDur = Math.round((new Date(b.endTime) - new Date(b.startTime)) / 1000);
            } else {
              // Active: pause by subtracting full elapsed
              const breakEnd = todayAttendance.logoutTime ? new Date(todayAttendance.logoutTime) : new Date();
              breakDur = Math.round((breakEnd - new Date(b.startTime)) / 1000);
            }
            deductSecs += breakDur;
          });
        }

        setCheckInTimer(Math.max(0, elapsed - deductSecs));
      };
      
      updateTimer();
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
      }
      if (!todayAttendance.logoutTime) {
        checkInIntervalRef.current = setInterval(updateTimer, 1000);
      }
    } else {
      setCheckInTimer(0);
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
        checkInIntervalRef.current = null;
      }
    }
    return () => {
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
        checkInIntervalRef.current = null;
      }
    };
  }, [todayAttendance, activeBreak, breakLimits]);

  const fetchTodayAttendance = async () => {
    try {
      const response = await api.get('/attendance/today');
      if (response.data) {
        setTodayAttendance(response.data);
        if (response.data.breaks && response.data.breaks.length > 0) {
          const ongoingBreak = response.data.breaks.find(b => !b.endTime);
          setActiveBreak(ongoingBreak || null);
        } else {
          setActiveBreak(null);
        }
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setTodayAttendance(null);
    }
  };

  const handleBreakStart = async (e) => {
    setHideBreakScreen(false);
    e.preventDefault();
    if (breakForm.type === 'Other' && !breakForm.reason.trim()) {
      alert('Please enter a reason for your break.');
      return;
    }
    try {
      const response = await api.post('/attendance/break/start', {
        breakType: breakForm.type,
        reason: breakForm.reason
      });
      if (response.data?.break) {
        setActiveBreak(response.data.break);
        setShowBreakModal(false);
        setBreakForm({ type: 'Tea Break', reason: '' });
        await fetchTodayAttendance();
      }
    } catch (error) {
      console.error('Error starting break:', error);
      alert(error.response?.data?.message || 'Failed to start break. Please try again.');
    }
  };

  const handleBreakEnd = async () => {
    try {
      await api.post('/attendance/break/end');
      setActiveBreak(null);
      setBreakTimer(0);
      await fetchTodayAttendance();
      alert('Break ended successfully.');
    } catch (error) {
      console.error('Error ending break:', error);
      alert(error.response?.data?.message || 'Failed to end break. Please try again.');
    }
  };

  const getTotalBreakDurationString = () => {
    let totalSecs = 0;
    if (todayAttendance?.breaks) {
      todayAttendance.breaks.forEach(b => {
        if (b.duration) {
          totalSecs += b.duration * 60;
        } else if (b.startTime && !b.endTime) {
          totalSecs += Math.floor((Date.now() - new Date(b.startTime).getTime()) / 1000);
        }
      });
    }
    if (activeBreak) {
      const inArray = todayAttendance?.breaks?.some(b => b.startTime === activeBreak.startTime);
      if (!inArray) {
        totalSecs += Math.floor((Date.now() - new Date(activeBreak.startTime).getTime()) / 1000);
      }
    }
    if (totalSecs === 0) return '0 mins';
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    if (hrs > 0) return `${hrs} hr ${mins} mins`;
    return `${mins} mins`;
  };

  const handleCheckIn = async () => {
    try {
      checkedInThisSession.current = true;
      setCheckInTimer(0);
      if (!checkInIntervalRef.current) {
        checkInIntervalRef.current = setInterval(() => {
          setCheckInTimer(prev => prev + 1);
        }, 1000);
      }
      const attendanceData = { method: 'self-checkin' };

      if (!navigator.geolocation) {
        checkedInThisSession.current = false;
        clearInterval(checkInIntervalRef.current);
        checkInIntervalRef.current = null;
        setCheckInTimer(0);
        alert('Location access is not supported by your browser. Check-in requires location.');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            address: `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`
          };
          api.post('/attendance/mark', { ...attendanceData, location: locationData })
            .then(response => {
              if (response.data) {
                setTodayAttendance(response.data);
                alert('Check-in successful!');
                fetchAll();
              }
            })
            .catch(err => {
              checkedInThisSession.current = false;
              clearInterval(checkInIntervalRef.current);
              checkInIntervalRef.current = null;
              setCheckInTimer(0);
              const msg = err.response?.data?.message || '';
              if (msg.toLowerCase().includes('away') || msg.toLowerCase().includes('office')) {
                alert('You can only check in inside the office.');
              } else {
                alert(msg || 'Failed to check in. Please try again.');
              }
            });
        },
        (geoErr) => {
          checkedInThisSession.current = false;
          clearInterval(checkInIntervalRef.current);
          checkInIntervalRef.current = null;
          setCheckInTimer(0);
          alert('Please give permission for location to check in.');
        },
        { timeout: 8000, enableHighAccuracy: true, maximumAge: 0 }
      );
    } catch (error) {
      console.error('Error checking in:', error);
      checkedInThisSession.current = false;
      clearInterval(checkInIntervalRef.current);
      checkInIntervalRef.current = null;
      setCheckInTimer(0);
      alert(error.response?.data?.message || 'Failed to check in. Please try again.');
    }
  };

  const handleCheckOut = async () => {
    try {
      checkedInThisSession.current = false;
      if (checkInIntervalRef.current) {
        clearInterval(checkInIntervalRef.current);
        checkInIntervalRef.current = null;
      }
      setCheckInTimer(0);

      if (!navigator.geolocation) {
        alert('Location access is not supported by your browser. Check-out requires location.');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            address: `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`
          };
          api.post('/attendance/checkout', { location: locationData })
            .then(response => {
              if (response.data) {
                setTodayAttendance(response.data);
                alert(`Checkout successful! Working Hours: ${response.data.workingHours} hrs | Status: ${response.data.status}`);
                fetchAll();
              }
            })
            .catch(err => {
              const msg = err.response?.data?.message || '';
              if (msg.toLowerCase().includes('away') || msg.toLowerCase().includes('office')) {
                alert('You can only check out inside the office.');
              } else {
                alert(msg || 'Failed to check out. Please try again.');
              }
            });
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Please give permission for location to check out.');
        },
        { timeout: 8000, enableHighAccuracy: true, maximumAge: 0 }
      );
    } catch (error) {
      console.error('Error checking out:', error);
      alert(error.response?.data?.message || 'Failed to check out. Please try again.');
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [
        metaStatsRes, dealStatsRes, myLeadsRes, leadAssignmentsRes, metaLeadsRes, analyticsRes, activitiesRes,
        attendanceRes, attendanceStatsRes, broadcastsRes, goalsRes, leavesRes, projectsRes, requestsRes, employeesRes
      ] = await Promise.all([
        api.get('/meta-leads/stats').catch(() => ({ data: { total: 0, byStatus: {} } })),
        api.get('/deals/stats/overview').catch(() => ({ data: {} })),
        api.get('/leads').catch(() => ({ data: [] })),
        api.get('/lead-assignments/my-leads').catch(() => ({ data: [] })),
        api.get('/meta-leads?limit=500').catch(() => ({ data: { leads: [] } })),
        api.get('/sales-reports/analytics/dashboard').catch(() => ({ data: {} })),
        api.get('/activities').catch(() => ({ data: [] })),
        api.get('/attendance/today').catch(() => ({ data: null })),
        api.get('/attendance/my-stats').catch(() => ({ data: {} })),
        api.get('/broadcasts').catch(() => ({ data: [] })),
        api.get('/goals').catch(() => ({ data: [] })),
        api.get('/leaves').catch(() => ({ data: [] })),
        api.get('/projects').catch(() => ({ data: [] })),
        api.get('/requests').catch(() => ({ data: [] })),
        api.get('/employees').catch(() => ({ data: [] })),
      ]);

      setLeadStats(metaStatsRes.data);
      setDealStats(dealStatsRes.data);
      
      const regularLeads = Array.isArray(myLeadsRes.data) ? myLeadsRes.data : [];
      const assignedLeads = Array.isArray(leadAssignmentsRes.data) ? leadAssignmentsRes.data : [];
      const combinedLeads = [...regularLeads, ...assignedLeads];
      setMyLeads(combinedLeads);
      
      const normalizedMetaLeads = Array.isArray(metaLeadsRes.data?.leads)
        ? metaLeadsRes.data.leads
        : Array.isArray(metaLeadsRes.data?.data)
          ? metaLeadsRes.data.data
          : Array.isArray(metaLeadsRes.data)
            ? metaLeadsRes.data
            : [];
      setMetaLeads(normalizedMetaLeads);
      setSalesAnalytics(analyticsRes.data || {});
      setRecentActivities((activitiesRes.data || []).slice(0, 8));

      setTodayAttendance(attendanceRes.data || null);
      if (attendanceRes.data && attendanceRes.data.breaks && attendanceRes.data.breaks.length > 0) {
        const ongoingBreak = attendanceRes.data.breaks.find(b => !b.endTime);
        setActiveBreak(ongoingBreak || null);
      } else {
        setActiveBreak(null);
      }
      setPersonalAttendance({
        presentDays: attendanceStatsRes.data?.presentDays || 0,
        absentDays: attendanceStatsRes.data?.absentDays || 0,
        halfDays: attendanceStatsRes.data?.halfDays || 0,
        lateMarks: attendanceStatsRes.data?.lateMarks || 0,
      });

      const activeProjects = projectsRes.data.filter(p => {
        const isActive = p.status === 'active' || p.status === 'on-hold';
        const isAssigned = p.assignedEmployees?.some(e => e._id === user._id || e === user._id) ||
          p.createdBy?._id === user._id || p.createdBy === user._id;
        return isActive && isAssigned;
      });
      setGeneralStats({
        activeProjects: activeProjects.length,
        upcomingDeadlines: projectsRes.data.filter(p => {
          if (p.deadline && p.status !== 'completed') {
            const d = new Date(p.deadline);
            const t = new Date(); t.setHours(0, 0, 0, 0);
            const e = new Date(t); e.setHours(23, 59, 59, 999);
            return d >= t && d <= e;
          }
          return false;
        }).length,
        goals: goalsRes.data.length || 0,
        broadcasts: broadcastsRes.data.length || 0,
        pendingApprovals: [...leavesRes.data.filter(l => l.status === 'pending'), ...requestsRes.data.filter(r => r.status === 'pending')].length,
        pendingLeaves: leavesRes.data.filter(l => l.status === 'pending').length,
      });

      computeWeeklyAnalytics(goalsRes.data, employeesRes.data, projectsRes.data);

      const sortedLeaves = [...leavesRes.data].sort((a, b) => new Date(b.createdAt || b.startDate) - new Date(a.createdAt || a.startDate));
      setRecentLeaves(sortedLeaves.slice(0, 5));

    } catch (err) {
      console.error('Sales dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const computeWeeklyAnalytics = (goalsData, employeesData, projectsData) => {
    const gData = Array.isArray(goalsData) ? goalsData : [];
    const eData = Array.isArray(employeesData) ? employeesData : [];
    const pData = Array.isArray(projectsData) ? projectsData : [];

    const daysLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const gc = [], cp = [], nj = [], ud = [];
    const now = new Date();
    const day = now.getDay();
    const mon = new Date(now);
    mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1));

    for (let i = 0; i < 7; i++) {
      const d = new Date(mon); d.setDate(mon.getDate() + i);
      const ds = new Date(d); ds.setHours(0, 0, 0, 0);
      const de = new Date(d); de.setHours(23, 59, 59, 999);

      let totalGoals = 0;
      gData.forEach(g => g.goals?.forEach(item => {
        if (item.isCompleted && item.completedAt) {
          const cd = new Date(item.completedAt);
          if (cd >= ds && cd <= de) totalGoals++;
        }
      }));

      gc.push(totalGoals);
      cp.push(pData.filter(p => p.status === 'completed' && p.updatedAt && new Date(p.updatedAt) >= ds && new Date(p.updatedAt) <= de).length);
      nj.push(eData.filter(e => { const jd = new Date(e.joiningDate || e.createdAt); return jd >= ds && jd <= de; }).length);
      ud.push(pData.filter(p => { if (!p.deadline) return false; const dl = new Date(p.deadline); dl.setHours(0, 0, 0, 0); return dl.getTime() === ds.getTime(); }).length);
    }
    setWeeklyAnalytics({ days: daysLabels, goalsCompleted: gc, completedProjects: cp, newJoining: nj, upcomingDeadlines: ud });
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const onResize = () => setIsNarrowScreen(window.innerWidth <= 1280);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Derived stats
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endOfDay = new Date(today);
  endOfDay.setHours(23, 59, 59, 999);

  const todayExcelLeads = myLeads.filter(l => {
    const d = new Date(l.createdAt);
    return d >= today && d <= endOfDay;
  }).length;

  const todayMetaLeads = metaLeads.filter(l => {
    if (Array.isArray(l.history)) {
      return l.history.some(h => {
        const hd = new Date(h.date);
        return (h.action === 'Assignment Update' || h.action === 'Assignment') && hd >= today && hd <= endOfDay;
      });
    }
    const d = new Date(l.updatedAt || l.createdAt);
    return d >= today && d <= endOfDay;
  }).length;

  const todayLeads = todayExcelLeads + todayMetaLeads;
  
  const normalizeLeadStatus = (status) => {
    if (!status || typeof status !== 'string') return 'New';
    const normalized = status.trim().toLowerCase();
    const statusMap = {
      new: 'New',
      contacted: 'Contacted',
      qualified: 'Qualified',
      interested: 'Interested',
      'follow-up': 'Follow-up',
      followup: 'Follow-up',
      converted: 'Converted',
      'not interested': 'Not Interested',
      notinterested: 'Not Interested',
      closed: 'Closed',
    };
    return statusMap[normalized] || 'New';
  };

  const combinedByStatus = useMemo(() => {
    const counts = {};

    (Array.isArray(metaLeads) ? metaLeads : []).forEach((lead) => {
      const key = normalizeLeadStatus(lead?.status);
      counts[key] = (counts[key] || 0) + 1;
    });

    (Array.isArray(myLeads) ? myLeads : []).forEach((lead) => {
      const key = normalizeLeadStatus(lead?.status);
      counts[key] = (counts[key] || 0) + 1;
    });

    if (Object.keys(counts).length === 0 && leadStats.byStatus) {
      Object.entries(leadStats.byStatus).forEach(([status, value]) => {
        const key = normalizeLeadStatus(status);
        counts[key] = (counts[key] || 0) + (Number(value) || 0);
      });
    }

    return counts;
  }, [metaLeads, myLeads, leadStats.byStatus]);

  const listBasedTotalLeads = (Array.isArray(metaLeads) ? metaLeads.length : 0) + (Array.isArray(myLeads) ? myLeads.length : 0);
  const totalLeads = listBasedTotalLeads > 0 ? listBasedTotalLeads : ((leadStats.total || 0) + myLeads.length);
  const convertedLeads = combinedByStatus.Converted || 0;
  const winRate = dealStats.winRate || (totalLeads > 0 ? ((convertedLeads / totalLeads) * 100).toFixed(1) : 0);
  const closedWon = dealStats.closedWon || 0;

  // Pipeline stages data
  const stageOrder = ['New', 'Contacted', 'Qualified', 'Interested', 'Follow-up', 'Converted', 'Not Interested', 'Closed'];
  const stageData = useMemo(
    () => stageOrder.map((s) => combinedByStatus[s] || 0),
    [combinedByStatus]
  );

  // Charts hooks
  useEffect(() => {
    if (loading) return;
    if (pipelineChartRef.current) {
      if (pipelineChartInst.current) pipelineChartInst.current.destroy();
      const ctx = pipelineChartRef.current.getContext('2d');
      
      const gradients = [
        { start: '#14532d', end: '#166534' },
        { start: '#166534', end: '#15803d' },
        { start: '#15803d', end: '#16a34a' },
        { start: '#16a34a', end: '#22c55e' },
        { start: '#65a30d', end: '#84cc16' },
        { start: '#15803d', end: '#22c55e' },
        { start: '#ef4444', end: '#dc2626' },
        { start: '#6b7280', end: '#4b5563' },
      ];
      
      const backgroundColors = gradients.map(grad => {
        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, grad.start);
        gradient.addColorStop(1, grad.end);
        return gradient;
      });
      
      pipelineChartInst.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: stageOrder,
          datasets: [{
            label: 'Leads',
            data: stageData,
            backgroundColor: backgroundColors,
            borderRadius: 8,
            borderSkipped: false,
            barThickness: window.innerWidth <= 480 ? 20 : window.innerWidth <= 768 ? 28 : 40,
            hoverBackgroundColor: [
              '#14532d', '#166534', '#15803d', '#16a34a',
              '#65a30d', '#22c55e', '#dc2626', '#4b5563'
            ],
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(15, 23, 42, 0.95)',
              titleFont: { size: 15, weight: 'bold', family: 'Inter' },
              bodyFont: { size: 14, weight: 'bold', family: 'Inter' },
              padding: 14,
              cornerRadius: 8,
              borderColor: '#166534',
              borderWidth: 2,
              displayColors: true,
              boxWidth: 12,
              boxHeight: 12,
              boxPadding: 6,
              callbacks: {
                title: (items) => items[0].label,
                label: (item) => ` ${item.formattedValue} Leads`
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: { 
                color: '#64748b', 
                font: { size: 13, weight: 'bold', family: 'Inter' }, 
                stepSize: 1,
                padding: 8
              },
              grid: { 
                color: 'rgba(226, 232, 240, 0.5)', 
                drawBorder: false,
                lineWidth: 1
              },
              border: { display: false }
            },
            x: {
              ticks: { 
                color: '#1f2937', 
                font: { size: 12, weight: 'bold', family: 'Inter' }, 
                maxRotation: 45,
                minRotation: 45,
                padding: 8
              },
              grid: { display: false },
              border: { display: false }
            }
          },
          animation: {
            duration: 800,
            easing: 'easeInOutQuart'
          }
        }
      });
    }
    return () => { if (pipelineChartInst.current) pipelineChartInst.current.destroy(); };
  }, [loading, stageData]);

  useEffect(() => {
    if (loading) return;
    if (leadStagesChartRef.current) {
      if (leadStagesChartInst.current) leadStagesChartInst.current.destroy();
      const ctx = leadStagesChartRef.current.getContext('2d');
      const activeStages = stageOrder.filter(s => (combinedByStatus[s] || 0) > 0);
      const activeData = activeStages.map(s => combinedByStatus[s]);
      const colors = {
        New: '#14532d', Contacted: '#166534', Qualified: '#15803d', Interested: '#16a34a',
        'Follow-up': '#65a30d', Converted: '#22c55e', 'Not Interested': '#ef4444', Closed: '#6b7280'
      };
      leadStagesChartInst.current = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: activeStages.length > 0 ? activeStages : ['No Data'],
          datasets: [{
            data: activeStages.length > 0 ? activeData : [1],
            backgroundColor: activeStages.length > 0 ? activeStages.map(s => colors[s]) : ['#e2e8f0'],
            borderWidth: 4,
            borderColor: '#fff',
            hoverOffset: 8,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '65%',
          plugins: {
            legend: {
              position: window.innerWidth <= 768 ? 'bottom' : 'right',
              labels: {
                color: '#1f2937',
                font: { size: window.innerWidth <= 480 ? 11 : 13, weight: '700' },
                padding: window.innerWidth <= 480 ? 10 : 16,
                usePointStyle: true,
                pointStyleWidth: 12,
              }
            },
            tooltip: {
              backgroundColor: '#0f172a',
              titleFont: { size: 14, weight: '700' },
              bodyFont: { size: 13, weight: '600' },
              padding: 12,
              cornerRadius: 0,
              borderColor: '#dbe6d5',
              borderWidth: 1,
            }
          }
        }
      });
    }
    return () => { if (leadStagesChartInst.current) leadStagesChartInst.current.destroy(); };
  }, [loading, combinedByStatus]);

  // Personal Attendance Chart
  useEffect(() => {
    if (loading || !personalChartRef.current) return;
    if (personalChartInst.current) personalChartInst.current.destroy();
    const ctx = personalChartRef.current.getContext('2d');
    personalChartInst.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: ['Present', 'Absent', 'Half Day', 'Late'],
        datasets: [{
          label: 'Days',
          data: [personalAttendance.presentDays, personalAttendance.absentDays, personalAttendance.halfDays, personalAttendance.lateMarks],
          borderColor: '#166534',
          backgroundColor: 'rgba(22,101,52,0.12)',
          borderWidth: 3, tension: 0.4, fill: true,
          pointBackgroundColor: ['#22c55e', '#ef4444', '#65a30d', '#14532d'],
          pointBorderColor: '#fff', pointBorderWidth: 3, pointRadius: 8, pointHoverRadius: 10,
          pointStyle: 'rect'
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { 
          legend: { display: false }, 
          tooltip: { 
            backgroundColor: '#0f172a', 
            padding: 12, 
            cornerRadius: 0,
            borderColor: '#dbe6d5',
            borderWidth: 1,
            titleFont: { size: 14, weight: '700' },
            bodyFont: { size: 13, weight: '600' }
          } 
        },
        scales: {
          y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 12, weight: '600' }, color: '#4b5563' }, grid: { color: '#eef3ea' } },
          x: { ticks: { font: { size: 12, weight: '700' }, color: '#1f2937' }, grid: { display: false } }
        }
      }
    });
    return () => { if (personalChartInst.current) personalChartInst.current.destroy(); };
  }, [loading, personalAttendance]);

  // Weekly Analytics Chart
  useEffect(() => {
    if (loading || !weeklyChartRef.current || weeklyAnalytics.days.length === 0) return;
    if (weeklyChartInst.current) weeklyChartInst.current.destroy();
    const ctx = weeklyChartRef.current.getContext('2d');
    weeklyChartInst.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: weeklyAnalytics.days,
        datasets: [
          { label: 'Goals Completed', data: weeklyAnalytics.goalsCompleted, backgroundColor: '#166534', borderRadius: 0, barThickness: 16 },
          { label: 'Completed Projects', data: weeklyAnalytics.completedProjects, backgroundColor: '#14532d', borderRadius: 0, barThickness: 16 },
          { label: 'New Joinings', data: weeklyAnalytics.newJoining, backgroundColor: '#65a30d', borderRadius: 0, barThickness: 16 },
          { label: 'Upcoming Deadlines', data: weeklyAnalytics.upcomingDeadlines, backgroundColor: '#22c55e', borderRadius: 0, barThickness: 16 },
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { 
            position: 'top', 
            labels: { 
              padding: 12, 
              boxWidth: 14, 
              font: { size: 11, weight: '700' }, 
              usePointStyle: true,
              color: '#1f2937'
            } 
          },
          tooltip: { 
            backgroundColor: '#0f172a', 
            padding: 12, 
            cornerRadius: 0,
            borderColor: '#dbe6d5',
            borderWidth: 1,
            titleFont: { size: 14, weight: '700' },
            bodyFont: { size: 13, weight: '600' }
          }
        },
        scales: {
          y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11, weight: '600' }, color: '#4b5563' }, grid: { color: '#eef3ea' } },
          x: { ticks: { font: { size: 11, weight: '700' }, color: '#1f2937' }, grid: { display: false } }
        }
      }
    });
    return () => { if (weeklyChartInst.current) weeklyChartInst.current.destroy(); };
  }, [loading, weeklyAnalytics]);

  // ––– Helpers –––
  const fmtCurrency = (v) => {
    if (!v) return '₹0';
    if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
    if (v >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
    return `₹${v}`;
  };
  
  const fmtDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };
  
  const timeAgo = (d) => {
    if (!d) return '';
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  // Top leads (recent with status)
  const metaRecentLeads = (Array.isArray(metaLeads) ? metaLeads : []).map((lead) => ({
    ...lead,
    _sourceType: 'Meta',
    _sourceLabel: lead.pageName || 'Meta',
    _sortDate: lead.createdTime || lead.createdAt,
  }));

  const excelRecentLeads = (Array.isArray(myLeads) ? myLeads : []).map((lead, index) => ({
    ...lead,
    _id: lead._id || `excel-lead-${index}`,
    name: lead.name || lead.company || lead.email || lead.contactNumber || 'Excel Lead',
    phone: lead.phone || lead.contactNumber,
    status: lead.status || 'New',
    pageName: lead.source || 'Excel',
    createdTime: lead.leadDate || lead.createdAt,
    _sourceType: 'Excel',
    _sourceLabel: lead.source || 'Excel',
    _sortDate: lead.leadDate || lead.createdAt,
  }));

  const allLeadsForTable = [...metaRecentLeads, ...excelRecentLeads]
    .sort((a, b) => new Date(b._sortDate || b.createdAt || 0) - new Date(a._sortDate || a.createdAt || 0));

  const recentLeads = allLeadsForTable.slice(0, 6);

  const statusColors = {
    New: { bg: '#ecfdf3', color: '#14532d' },
    Contacted: { bg: '#f0fdf4', color: '#166534' },
    Qualified: { bg: '#f7fee7', color: '#3f6212' },
    Interested: { bg: '#dcfce7', color: '#15803d' },
    'Follow-up': { bg: '#fff7ed', color: '#854d0e' },
    Converted: { bg: '#dcfce7', color: '#14532d' },
    'Not Interested': { bg: '#fef2f2', color: '#dc2626' },
    Closed: { bg: '#f8fafc', color: '#64748b' },
  };

  const activityIcons = {
    'Status Change': { icon: '🔄', color: '#166534' },
    'Assignment Update': { icon: '🤝', color: '#15803d' },
    'Note Added': { icon: '📝', color: '#65a30d' },
    'Follow-up Set': { icon: '📅', color: '#16a34a' },
    default: { icon: '📌', color: '#64748b' },
  };

  const attendanceStatus = todayAttendance
    ? (todayAttendance.logoutTime
        ? (todayAttendance.status === 'present'  ? 'Present'
           : todayAttendance.status === 'half-day' ? 'Half Day'
           : 'Absent')
        : (todayAttendance.status === 'late-checkin' ? 'Late Check-in' : todayAttendance.status === 'early-checkin' ? 'Early Check-in' : 'Checked In'))
    : 'Not Logged In';

  const workingHours = todayAttendance?.loginTime
    ? (() => {
        const totalSecs = Math.floor((new Date() - new Date(todayAttendance.loginTime)) / 1000);
        let breakSecs = 0;
        if (todayAttendance.breaks && todayAttendance.breaks.length > 0) {
          todayAttendance.breaks.forEach(b => {
            if (b.endTime) {
              breakSecs += Math.round((new Date(b.endTime) - new Date(b.startTime)) / 1000);
            } else {
              breakSecs += Math.round((Date.now() - new Date(b.startTime)) / 1000);
            }
          });
        }
        const netSecs = Math.max(0, totalSecs - breakSecs);
        const hrs = Math.floor(netSecs / 3600);
        const mins = Math.floor((netSecs % 3600) / 60);
        return `${hrs} hr ${mins} mins`;
      })()
    : '0 hr 0 mins';

  const userInitials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.toUpperCase() || 'SU';

  const attendanceMetrics = [
    {
      label: 'Login Time',
      value: todayAttendance?.loginTime
        ? new Date(todayAttendance.loginTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        : '--:--',
      sub: 'Session start'
    },
    { label: 'Working Hours', value: workingHours, sub: 'Live today' },
    {
      label: 'Break Time',
      value: getTotalBreakDurationString(),
      sub: 'Total breaks'
    },
    {
      label: 'Today Status',
      value: attendanceStatus,
      sub: attendanceStatus === 'Checked In' || attendanceStatus === 'Late Check-in' || attendanceStatus === 'Early Check-in' || attendanceStatus === 'Present' ? 'Active' : 'Inactive'
    }
  ];

  const kpiCards = [
    { label: "Today's Leads", value: todayLeads, sub: 'Assigned today', tone: 'dark' },
    { label: 'Total Leads', value: totalLeads, sub: 'Meta + Excel', tone: 'primary' },
    { label: 'Deals Closed', value: closedWon, sub: `${fmtCurrency(dealStats.wonValue || 0)} value`, tone: 'success' },
    { label: 'Win Rate', value: `${winRate}%`, sub: `${convertedLeads} converted`, tone: 'lime' },
  ];

  const focusCards = [
    { label: 'Active Projects', value: generalStats.activeProjects },
    { label: 'Deadlines', value: generalStats.upcomingDeadlines },
    { label: 'My Goals', value: generalStats.goals },
    { label: 'Broadcasts', value: generalStats.broadcasts },
    { label: 'Approvals', value: generalStats.pendingApprovals },
    { label: 'Pending Leaves', value: generalStats.pendingLeaves },
  ];

  const performanceMetrics = [
    {
      label: 'Total Calls',
      value: salesAnalytics.totalCalls || 0,
      width: 100,
      color: '#14532d'
    },
    {
      label: 'Connections',
      value: salesAnalytics.totalConnections || 0,
      width: Math.min(salesAnalytics.connectionRate || 0, 100),
      color: '#166534'
    },
    {
      label: 'Qualified',
      value: salesAnalytics.totalQualifiedProspects || 0,
      width: Math.min(salesAnalytics.conversionRate || 0, 100),
      color: '#22c55e'
    },
    {
      label: 'Avg Score',
      value: (salesAnalytics.avgPerformanceScore || 0).toFixed(1),
      width: Math.min((salesAnalytics.avgPerformanceScore || 0) * 10, 100),
      color: '#84cc16'
    }
  ];

  const visibleStages = useMemo(
    () => stageOrder
      .map((stage) => ({ stage, value: combinedByStatus[stage] || 0 }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value),
    [combinedByStatus]
  );

  const profileCard = (
    <section className="sd-profile-card">
      <div className="sd-profile-flip">
        <div className="sd-profile-face sd-profile-front">
          <div className="sd-profile-top">
            <div className="sd-avatar">{userInitials}</div>
            <div className="sd-profile-copy">
              <span className="sd-overline">Dashboard User</span>
              <h2>{`${user?.firstName || 'Sales'} ${user?.lastName || 'User'}`.trim()}</h2>
              <p>{user?.email || 'sales@company.com'}</p>
            </div>
          </div>

          <div className="sd-presence-strip">
            <span className={`sd-presence-pill ${attendanceStatus === 'Present' || attendanceStatus === 'Half Day' ? 'is-present' : (attendanceStatus === 'Checked In' || attendanceStatus === 'Late Check-in') ? 'is-live' : 'is-offline'}`}>
              {attendanceStatus}
            </span>
          </div>

          <div className="sd-profile-grid">
            {attendanceMetrics.map((item) => (
              <div className="sd-profile-metric" key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
                <small>{item.sub}</small>
              </div>
            ))}
          </div>
        </div>

        <div className="sd-profile-face sd-profile-back">
          <div className="sd-profile-back-head">
            <span className="sd-overline">Quick Snapshot</span>
            <h3>{`${user?.firstName || 'Sales'} Summary`}</h3>
          </div>

          <div className="sd-profile-back-grid">
            <div className="sd-profile-back-item">
              <span>Today's Leads</span>
              <strong>{todayLeads}</strong>
              <small>assigned today</small>
            </div>
            <div className="sd-profile-back-item">
              <span>Total Leads</span>
              <strong>{totalLeads}</strong>
              <small>meta + excel</small>
            </div>
            <div className="sd-profile-back-item">
              <span>Deals Closed</span>
              <strong>{closedWon}</strong>
              <small>{fmtCurrency(dealStats.wonValue || 0)} value</small>
            </div>
            <div className="sd-profile-back-item">
              <span>Win Rate</span>
              <strong>{winRate}%</strong>
              <small>{convertedLeads} converted</small>
            </div>
          </div>

          <div className="sd-profile-back-tip">Hover out to return to attendance view.</div>
        </div>
      </div>
    </section>
  );

  if (loading) {
    return (
      <div className="sd-container">
        <div className="sd-shell">
          <aside className="sd-rail">
            <section className="sd-profile-card sd-skeleton-card">
              <div className="sd-profile-top">
                <div className="sd-avatar sd-skeleton" />
                <div className="sd-profile-copy">
                  <div className="sd-skeleton sd-skel-overline" />
                  <div className="sd-skeleton sd-skel-title" />
                  <div className="sd-skeleton sd-skel-subtitle" />
                </div>
              </div>
              <div className="sd-profile-grid">
                {[...Array(4)].map((_, i) => (
                  <div className="sd-profile-metric sd-skeleton-metric" key={`profile-metric-${i}`}>
                    <div className="sd-skeleton sd-skel-line-sm" />
                    <div className="sd-skeleton sd-skel-value" />
                    <div className="sd-skeleton sd-skel-line-xs" />
                  </div>
                ))}
              </div>
            </section>

            {[...Array(2)].map((_, i) => (
              <section className="sd-side-card sd-skeleton-card" key={`side-skeleton-${i}`}>
                <div className="sd-side-head">
                  <div className="sd-skeleton-group">
                    <div className="sd-skeleton sd-skel-overline" />
                    <div className="sd-skeleton sd-skel-subtitle" />
                  </div>
                  <div className="sd-skeleton sd-skel-badge" />
                </div>
                <div className="sd-focus-list">
                  {[...Array(3)].map((__, j) => (
                    <div className="sd-focus-item sd-skeleton-item" key={`focus-skeleton-${i}-${j}`}>
                      <div className="sd-skeleton sd-skel-line" />
                      <div className="sd-skeleton sd-skel-value-sm" />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </aside>

          <main className="sd-main">
            <div className="sd-header">
              <div className="sd-header-left sd-skeleton-group">
                <div className="sd-skeleton sd-skel-overline" />
                <div className="sd-skeleton sd-skel-hero-title" />
                <div className="sd-skeleton sd-skel-line-lg" />
              </div>
              <div className="sd-header-right">
                <div className="sd-skeleton sd-skel-date" />
                <div className="sd-skeleton sd-skel-btn" />
              </div>
            </div>

            <section className="sd-kpi-grid">
              {[...Array(4)].map((_, i) => (
                <article className="sd-kpi-card sd-skeleton-card" key={`kpi-skeleton-${i}`}>
                  <div className="sd-kpi-copy sd-skeleton-group">
                    <div className="sd-skeleton sd-skel-overline" />
                    <div className="sd-skeleton sd-skel-kpi-value" />
                    <div className="sd-skeleton sd-skel-line-sm" />
                  </div>
                  <div className="sd-skeleton sd-skel-ring" />
                </article>
              ))}
            </section>

            <section className="sd-feature-grid">
              {[...Array(2)].map((_, i) => (
                <article className="sd-panel sd-skeleton-card" key={`feature-skeleton-${i}`}>
                  <div className="sd-panel-head">
                    <div className="sd-skeleton-group">
                      <div className="sd-skeleton sd-skel-overline" />
                      <div className="sd-skeleton sd-skel-subtitle" />
                    </div>
                    <div className="sd-skeleton sd-skel-badge" />
                  </div>
                  <div className="sd-skeleton sd-skel-chart" />
                </article>
              ))}
            </section>
          </main>
        </div>
      </div>
    );
  }

  let totalBreakMins = 0;
  if (breakDetailsModal.breaks) {
    breakDetailsModal.breaks.forEach(b => {
      if (b.breakType !== 'Other') {
        const dur = b.duration !== undefined ? b.duration : Math.max(0, Math.floor((Date.now() - new Date(b.startTime).getTime()) / 60000));
        totalBreakMins += dur;
      }
    });
  }
  const limitMins = breakLimits?.totalBreakLimit || 60;
  const timeLeftMins = Math.max(0, limitMins - totalBreakMins);

  return (
    <div className="sd-container">
      <div className="sd-shell">
        <aside className="sd-rail">
          {!isNarrowScreen && profileCard}

          <section className="sd-side-card">
            <div className="sd-side-head">
              <div>
                <span className="sd-overline">Overview</span>
                <h3>Quick Focus</h3>
              </div>
              <span className="sd-side-badge">Live</span>
            </div>
            <div className="sd-focus-list">
              {focusCards.map((item) => (
                <div className="sd-focus-item" key={item.label}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </section>

          {(salesAnalytics.totalCalls > 0 || salesAnalytics.totalConnections > 0) && (
            <section className="sd-side-card">
              <div className="sd-side-head">
                <div>
                  <span className="sd-overline">Performance</span>
                  <h3>Sales Pulse</h3>
                </div>
                <span className="sd-side-badge">Tracked</span>
              </div>
              <div className="sd-progress-list">
                {performanceMetrics.map((metric) => (
                  <div className="sd-progress-item" key={metric.label}>
                    <div className="sd-progress-copy">
                      <span>{metric.label}</span>
                      <strong>{metric.value}</strong>
                    </div>
                    <div className="sd-progress-bar">
                      <div className="sd-progress-fill" style={{ width: `${metric.width}%`, background: metric.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="sd-side-card">
            <div className="sd-side-head">
              <div>
                <span className="sd-overline">Timeline</span>
                <h3>Recent Activity</h3>
              </div>
              <span className="sd-side-badge">{recentActivities.length} items</span>
            </div>
            <div className="sd-activity-list sd-activity-list-compact">
              {recentActivities.length > 0 ? recentActivities.slice(0, 5).map((act, index) => {
                const cfg = activityIcons[act.type] || activityIcons.default;
                return (
                  <div className="sd-activity-item" key={index}>
                    <div className="sd-activity-dot" style={{ background: act.color || cfg.color }}>
                      {act.icon || cfg.icon}
                    </div>
                    <div className="sd-activity-content">
                      <p className="sd-activity-title">{act.title || act.description || 'Activity'}</p>
                      <span className="sd-activity-time">{timeAgo(act.timestamp || act.date)}</span>
                    </div>
                  </div>
                );
              }) : (
                <div className="sd-empty-activity">No recent activity</div>
              )}
            </div>
          </section>
        </aside>

        <main className="sd-main">
          <div className="sd-header">
            <div className="sd-header-left">
              <span className="sd-overline">Sales Overview</span>
              <h1 className="sd-title">Sales Dashboard</h1>
              <p className="sd-subtitle">Saara existing data same hai, bas cleaner analytics layout me arranged hai.</p>
            </div>
            <div className="sd-header-right">
              <span className="sd-date-badge">
                {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              {!todayAttendance && (
                <button
                  onClick={handleCheckIn}
                  className="sd-btn-checkin"
                >
                  <LogIn size={16} /> Check In
                </button>
              )}
              {todayAttendance?.loginTime && !todayAttendance?.logoutTime && (
                <div className="sd-checkout-group">
                  <span className="sd-timer">
                    {new Date(checkInTimer * 1000).toISOString().substr(11, 8)}
                  </span>
                  <button
                    onClick={handleCheckOut}
                    className="sd-btn-checkout"
                  >
                    <LogIn size={14} style={{ transform: 'rotate(180deg)' }} /> Check Out
                  </button>
                </div>
              )}
              <button className="sd-btn-refresh" onClick={fetchAll}>Refresh</button>
              {/* --- BREAK BUTTONS --- */}
              {todayAttendance?.loginTime && !todayAttendance?.logoutTime && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {activeBreak ? (
                    <button
                      onClick={handleBreakEnd}
                      className="sd-btn-checkout"
                      style={{ background: '#ef4444' }}
                    >
                      Stop Break ({new Date(breakTimer * 1000).toISOString().substr(11, 8)})
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowBreakModal(true)}
                      className="sd-btn-checkin"
                      style={{ background: '#f59e0b', borderColor: 'transparent', color: '#000' }}
                    >
                      Take Break
                    </button>
                  )}
                  
                  
                        {(() => {
                          if (!todayAttendance || !todayAttendance.loginTime || todayAttendance.logoutTime) return null;
                          return (
                            <div
                              onClick={() => setBreakDetailsModal({ visible: true, type: 'All Breaks', breaks: todayAttendance.breaks || [] })}
                              style={{
                                background: '#c4ef4e', color: '#1e293b', padding: '0 16px', height: '36px', borderRadius: '999px',
                                fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', boxSizing: 'border-box', cursor: 'pointer', gap: '6px'
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect><path d="M12 11h4"></path><path d="M12 16h4"></path><path d="M8 11h.01"></path><path d="M8 16h.01"></path></svg> Break Details
                            </div>
                          );
                        })()}
                </div>
              )}

            </div>
          </div>

          {isNarrowScreen && <div className="sd-main-profile-slot">{profileCard}</div>}

          <section className="sd-kpi-grid">
            {kpiCards.map((card, index) => (
              <article className={`sd-kpi-card tone-${card.tone}`} key={card.label}>
                <div className="sd-kpi-copy">
                  <span>{card.label}</span>
                  <strong>{card.value}</strong>
                  <small>{card.sub}</small>
                </div>
                <div className="sd-kpi-visual">
                  {index === 3 ? (
                    <div className="sd-kpi-ring">
                      <svg viewBox="0 0 44 44">
                        <circle cx="22" cy="22" r="16" pathLength="100" className="sd-kpi-ring-track" />
                        <circle
                          cx="22"
                          cy="22"
                          r="16"
                          pathLength="100"
                          className="sd-kpi-ring-value"
                          style={{ strokeDasharray: `${Math.min(parseFloat(winRate) || 0, 100)} 100` }}
                        />
                      </svg>
                    </div>
                  ) : (
                    <div className="sd-kpi-bars">
                      {stageData.slice(index, index + 4).map((value, barIndex) => (
                        <span
                          key={`${card.label}-${barIndex}`}
                          style={{ height: `${Math.max(value * 8, 10)}px` }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </section>

          <section className="sd-feature-grid">
            <article className="sd-panel sd-panel-hero">
              <div className="sd-panel-head">
                <div>
                  <span className="sd-overline">Main Report</span>
                  <h3>Lead Pipeline</h3>
                </div>
                <span className="sd-panel-badge">This Month</span>
              </div>
              <div className="sd-chart-body sd-chart-body-lg">
                <canvas ref={pipelineChartRef} />
              </div>
              <div className="sd-hero-footer">
                <div>
                  <span>Total Leads</span>
                  <strong>{totalLeads}</strong>
                </div>
                <div>
                  <span>Converted</span>
                  <strong>{convertedLeads}</strong>
                </div>
                <div>
                  <span>Won Value</span>
                  <strong>{fmtCurrency(dealStats.wonValue || 0)}</strong>
                </div>
              </div>
            </article>

            <article className="sd-panel sd-panel-stage">
              <div className="sd-panel-head">
                <div>
                  <span className="sd-overline">Distribution</span>
                  <h3>Lead Stages</h3>
                </div>
                <span className="sd-panel-badge">{totalLeads} total</span>
              </div>
              <div className="sd-stage-layout">
                <div className="sd-stage-chart">
                  <canvas ref={leadStagesChartRef} />
                </div>
                <div className="sd-stage-list">
                  {visibleStages.length > 0 ? visibleStages.slice(0, 6).map((item) => (
                    <div className="sd-stage-item" key={item.stage}>
                      <span>{item.stage}</span>
                      <strong>{item.value}</strong>
                    </div>
                  )) : (
                    <div className="sd-stage-empty">No stage data available.</div>
                  )}
                </div>
              </div>
            </article>
          </section>

          <section className="sd-content-grid">
            <article className="sd-panel sd-panel-table">
              <div className="sd-panel-head">
                <div>
                  <span className="sd-overline">Fresh Leads</span>
                  <h3>Recent Leads</h3>
                </div>
                <span className="sd-panel-badge">{allLeadsForTable.length} total</span>
              </div>
              <div className="sd-table-wrap">
                <table className="sd-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Source</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLeads.length > 0 ? recentLeads.map((lead) => (
                      <tr key={lead._id}>
                        <td>
                          <div className="sd-lead-name">{lead.name || '—'}</div>
                          {lead.city && <div className="sd-lead-sub">{lead.city}</div>}
                        </td>
                        <td>
                          <div className="sd-lead-phone">{lead.phone || '—'}</div>
                          {lead.email && <div className="sd-lead-sub">{lead.email}</div>}
                        </td>
                        <td><span className="sd-source-badge">{lead._sourceLabel || lead.pageName || 'Meta'}</span></td>
                        <td>
                          <span
                            className="sd-status-pill"
                            style={{
                              background: statusColors[lead.status]?.bg || '#f8fafc',
                              color: statusColors[lead.status]?.color || '#64748b',
                            }}
                          >
                            {lead.status || 'New'}
                          </span>
                        </td>
                        <td className="sd-date-cell">{fmtDate(lead.createdTime || lead.createdAt || lead._sortDate)}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="5" className="sd-empty-row">No leads yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            <div className="sd-panel-stack">
              <article className="sd-panel">
                <div className="sd-panel-head">
                  <div>
                    <span className="sd-overline">Attendance</span>
                    <h3>Personal Attendance</h3>
                  </div>
                  <span className="sd-panel-badge">This month</span>
                </div>
                <div className="sd-chart-wrap sd-chart-medium">
                  <canvas ref={personalChartRef} />
                </div>
              </article>
            </div>
          </section>

          {recentLeaves.length > 0 && (
            <section className="sd-panel sd-panel-leaves">
              <div className="sd-panel-head">
                <div>
                  <span className="sd-overline">Approvals</span>
                  <h3>Recent Leave Applications</h3>
                </div>
                <span className="sd-panel-badge">{recentLeaves.length} entries</span>
              </div>
              <div className="sd-leaves-table-wrap">
                <table className="sd-leaves-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Type</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLeaves.map((lv, index) => (
                      <tr key={index}>
                        <td>{lv.employeeName || lv.employee?.name || lv.userId?.name || '—'}</td>
                        <td>{lv.leaveType || lv.type || '—'}</td>
                        <td>{fmtDate(lv.startDate || lv.from)}</td>
                        <td>{fmtDate(lv.endDate || lv.to)}</td>
                        <td>
                          <span className={`sd-leave-badge sd-leave-${(lv.status || 'pending').toLowerCase()}`}>
                            {lv.status || 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </main>
      </div>

      {/* Break Selection Modal */}
      {showBreakModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(3px)' }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', boxShadow: '0 8px 32px rgba(15,23,42,0.13)', padding: '20px', width: '400px', maxWidth: '90vw' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#0f172a' }}>Take a Break</h3>
            <form onSubmit={handleBreakStart}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>Break Type <span style={{ color: '#ef4444' }}>*</span></label>
                <BreakDropdown 
                  value={breakForm.type}
                  onChange={(val) => setBreakForm({...breakForm, type: val})}
                />
              </div>
              
              {breakForm.type === 'Other' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>Reason <span style={{ color: '#ef4444' }}>*</span></label>
                  <textarea 
                    value={breakForm.reason}
                    onChange={(e) => setBreakForm({...breakForm, reason: e.target.value})}
                    placeholder="Enter reason for break"
                    rows="3"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#0f172a', background: '#f8fafc', outline: 'none', resize: 'vertical' }}
                    required
                  />
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <button type="button" onClick={() => setShowBreakModal(false)} style={{ padding: '8px 16px', border: '1px solid #e2e8f0', background: '#fff', borderRadius: '8px', color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                <button type="submit" style={{ padding: '8px 16px', border: 'none', background: '#f59e0b', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Start Break</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Break Details Info Modal */}
      {breakDetailsModal.visible && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(3px)' }} onClick={() => setBreakDetailsModal({ visible: false, type: '', breaks: [] })}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', boxShadow: '0 8px 32px rgba(15,23,42,0.13)', padding: '24px', width: '400px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: '0', fontSize: '18px', color: '#0f172a' }}>{breakDetailsModal.type} Details</h3>
              <button onClick={() => setBreakDetailsModal({ visible: false, type: '', breaks: [] })} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <XCircle size={20} />
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                <Clock size={24} strokeWidth={1.5} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Time Remaining</span>
                  <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Time Taken</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: '20px', color: '#0f172a', fontWeight: 600, letterSpacing: '-0.5px' }}>{timeLeftMins} <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500, letterSpacing: '0' }}>mins</span></span>
                  <span style={{ fontSize: '16px', color: '#475569', fontWeight: 600 }}>{totalBreakMins} <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 500 }}>mins</span></span>
                </div>
              </div>
            </div>

            {breakDetailsModal.breaks && breakDetailsModal.breaks.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {breakDetailsModal.breaks.map((b, idx) => {
                  const dur = b.duration !== undefined ? b.duration : Math.max(0, Math.floor((Date.now() - new Date(b.startTime).getTime()) / 60000));
                  return (
                    <div key={idx} style={{ padding: '16px', background: '#fff', borderRadius: '12px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px', boxShadow: '0 2px 4px rgba(15,23,42,0.02)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: b.endTime ? '#cbd5e1' : '#10b981' }}></div>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', marginBottom: '2px' }}>{b.breakType || `Break ${idx + 1}`}</div>
                            <div style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Hourglass size={12} />
                              {new Date(b.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} 
                              {' - '} 
                              {b.endTime ? new Date(b.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Now'}
                            </div>
                          </div>
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>
                          {dur} mins
                        </div>
                      </div>
                      {b.reason && (
                        <div style={{ fontSize: '12px', color: '#475569', padding: '6px 8px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', marginLeft: '20px' }}>
                          <strong style={{ color: '#334155' }}>Reason:</strong> {b.reason}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: '#64748b', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>No {breakDetailsModal.type}s taken yet.</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button 
                onClick={() => setBreakDetailsModal({ visible: false, type: '', breaks: [] })}
                style={{ padding: '8px 16px', border: 'none', background: '#10b981', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Break Glassmorphism Screen */}
      {activeBreak && !hideBreakScreen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.4s ease'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '24px', opacity: 0.8 }}>
              <Clock size={40} color="#334155" strokeWidth={1.5} />
            </div>
            <h1 style={{ fontSize: '26px', color: '#0f172a', margin: '0 0 6px 0', fontWeight: 600, letterSpacing: '-0.3px' }}>On Break</h1>
            <p style={{ fontSize: '15px', color: '#475569', margin: '0 0 36px 0', fontWeight: 500 }}>{activeBreak.type}</p>
            
            <div style={{ fontSize: '64px', fontWeight: '300', color: '#0f172a', fontVariantNumeric: 'tabular-nums', margin: '0 0 44px 0', letterSpacing: '-1.5px' }}>
               {new Date(breakTimer * 1000).toISOString().substr(11, 8)}
            </div>
            
            <button 
               onClick={() => setHideBreakScreen(true)}
               style={{ padding: '12px 40px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)', transition: 'all 0.2s ease' }}
               onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(245, 158, 11, 0.35)'; e.currentTarget.style.background = '#d97706'; }}
               onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.25)'; e.currentTarget.style.background = '#f59e0b'; }}
            >
               Resume Work
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalesDashboard;
